import { db, Auth, firebase } from "@/common/firebase/init";

/**
 * --------------------------------------------------------------
 * Fitur indikator typing untuk dashboard staff.
 *
 * Action publik:
 * JOIN_THREAD   : memiliki satu sesi listener/presence Firebase.
 * LEAVE_THREAD  : membatalkan sesi dan membersihkan resource.
 * HANDLE_TYPING : mengirim status typing dan memperbarui timer.
 * STOP_TYPING   : langsung mengirim ts=0.
 * --------------------------------------------------------------
 */

export const JOIN_THREAD = "joinThread";
export const LEAVE_THREAD = "leaveThread";
export const HANDLE_TYPING = "handleTyping";
export const STOP_TYPING = "stopTyping";

const ADD_USER = "addUser";
const REMOVE_USER = "removeUser";
const CLEAR_THREAD = "clearTypingThread";
const UPDATE_TS = "updateTs";
const RESET_TS = "resetTs";
const RESET_TIMER = "resetTimer";

/**
 * --------------------------------------------------------------
 * Kontrak waktu. Interval refresh harus di bawah batas stale agar
 * typing terus tampil, tetapi event stop/remove yang hilang tetap expired.
 * --------------------------------------------------------------
 */
export const STALE_FILTER_MS = 4000;
export const CLIENT_STOP_MS = 3000;
export const TYPING_REFRESH_MS = 2000;

/**
 * --------------------------------------------------------------
 * Kepemilikan runtime. Generation membatalkan lanjutan proses async dari
 * join lama. Hanya _activeSession yang boleh mengubah state UI.
 * --------------------------------------------------------------
 */
let _generation = 0;
let _activeSession = null;
const _expiryTimers = new Map();

const expiryKey = (threadId, uid) => `${threadId}:${uid}`;

const lifecycleKey = (session) =>
  `threads/${session.threadId}/typing/${session.uid}`;

const clearExpiryTimer = (threadId, uid) => {
  const key = expiryKey(threadId, uid);
  const timer = _expiryTimers.get(key);
  clearTimeout(timer);
  _expiryTimers.delete(key);
};

const clearThreadExpiryTimers = (threadId) => {
  const prefix = `${threadId}:`;
  _expiryTimers.forEach((timer, key) => {
    if (key.startsWith(prefix)) {
      clearTimeout(timer);
      _expiryTimers.delete(key);
    }
  });
};

const isCurrentSession = (session) =>
  _activeSession === session &&
  session.generation === _generation &&
  !session.disposed;

/**
 * --------------------------------------------------------------
 * Satu session memiliki seluruh resource untuk satu thread terpilih.
 * Penyimpanan ref di sini mencegah cleanup membaca active thread yang berubah.
 * --------------------------------------------------------------
 */
const createSession = (threadId, user, generation) => ({
  generation,
  threadId,
  uid: user.uid,
  name: user.displayName?.split(" ")[0] ?? "Unknown",
  presenceRef: db.ref(`threads/${threadId}/typing/${user.uid}`),
  typingRef: db.ref(`threads/${threadId}/typing`),
  unsubscribe: null,
  stopTimer: null,
  writeChain: Promise.resolve(),
  desiredTyping: false,
  lastTypingWriteAt: 0,
  isTyping: false,
  joined: false,
  disposed: false,
  disposePromise: null,
});

/** Cleanup lama tidak boleh menghapus session baru pada path yang sama. */
const isPresenceOwnedByAnotherSession = (session) => {
  const current = _activeSession;
  return (
    current &&
    current !== session &&
    !current.disposed &&
    lifecycleKey(current) === lifecycleKey(session)
  );
};

/** Cleanup remote best-effort; listener lokal sudah dilepas lebih dahulu. */
const cleanupPresence = async (session, removePresence = true) => {
  if (isPresenceOwnedByAnotherSession(session)) return;

  try {
    await session.presenceRef.onDisconnect().cancel();
  } catch (err) {
    console.error("[typing] cancel onDisconnect error:", err);
  }

  if (!removePresence || isPresenceOwnedByAnotherSession(session)) return;

  try {
    await session.presenceRef.remove();
  } catch (err) {
    console.error("[typing] remove presence error:", err);
  }
};

/** Membersihkan listener, timer, dan presence secara idempotent. */
const disposeSession = (session, { removePresence = true } = {}) => {
  if (!session) return Promise.resolve();
  if (session.disposePromise) return session.disposePromise;

  session.disposed = true;
  session.joined = false;
  session.desiredTyping = false;

  if (session.unsubscribe) {
    session.unsubscribe();
    session.unsubscribe = null;
  }

  if (session.stopTimer) {
    clearTimeout(session.stopTimer);
    session.stopTimer = null;
  }
  clearThreadExpiryTimers(session.threadId);

  session.disposePromise = cleanupPresence(session, removePresence);
  return session.disposePromise;
};

/**
 * Menyimpan typer pada thread sumber dan menghapusnya relatif dari event
 * yang diterima. Setiap refresh mengganti timer; ts=0 langsung menghapus.
 */
const scheduleExpiry = (commit, session, uid, payload) => {
  clearExpiryTimer(session.threadId, uid);

  if (payload.ts === 0) {
    commit(REMOVE_USER, { threadId: session.threadId, uid });
    return;
  }

  const name =
    typeof payload.name === "string" && payload.name.trim()
      ? payload.name
      : "Unknown";

  commit(ADD_USER, {
    threadId: session.threadId,
    uid,
    name,
    ts: payload.ts,
  });

  const key = expiryKey(session.threadId, uid);
  const timer = setTimeout(() => {
    _expiryTimers.delete(key);
    if (!isCurrentSession(session)) return;
    commit(REMOVE_USER, {
      threadId: session.threadId,
      uid,
      expectedTs: payload.ts,
    });
  }, STALE_FILTER_MS);
  _expiryTimers.set(key, timer);
};

const getCurrentSession = (threadId, generation) => {
  const session = _activeSession;
  if (!session || !isCurrentSession(session) || !session.joined) return null;
  if (String(session.threadId) !== String(threadId)) return null;
  if (generation !== undefined && generation !== session.generation)
    return null;
  return session;
};

/** Menserialisasi write agar stop selalu menang dari start yang pending. */
const enqueueTypingState = (session, typing) => {
  session.desiredTyping = typing;
  session.writeChain = session.writeChain
    .catch(() => undefined)
    .then(async () => {
      if (!isCurrentSession(session) || !session.joined) return false;

      const desiredTyping = session.desiredTyping;
      if (!desiredTyping && !session.isTyping) return true;

      if (desiredTyping) {
        const now = Date.now();
        if (
          session.isTyping &&
          now - session.lastTypingWriteAt < TYPING_REFRESH_MS
        ) {
          return true;
        }
        session.lastTypingWriteAt = now;
        await session.presenceRef.set({
          name: session.name,
          ts: firebase.database.ServerValue.TIMESTAMP,
        });
      } else {
        await session.presenceRef.update({ ts: 0 });
      }

      if (!isCurrentSession(session) || !session.joined) return false;
      session.isTyping = desiredTyping;
      return true;
    })
    .catch((err) => {
      console.error("[typing] update state error:", err);
      return false;
    });

  return session.writeChain;
};

/**
 * typingUsersByThread[threadId][uid] = { name, ts }
 * State per-thread mencegah callback bocor ke dialog lain.
 */
export default {
  state: {
    typingUsersByThread: {},
  },

  getters: {
    typingUserListByThread: (state) => (threadId) =>
      Object.values(state.typingUsersByThread[threadId] || {}).map(
        (entry) => entry.name
      ),
    hasTypingActivityByThread: (state) => (threadId) =>
      Object.keys(state.typingUsersByThread[threadId] || {}).length > 0,
  },

  actions: {
    /**
     * Join thread terpilih. Kepemilikan session diset sebelum proses async;
     * setiap lanjutan proses memeriksa generation dan session aktif.
     */
    [JOIN_THREAD]({ commit }, { threadId }) {
      const user = Auth.currentUser;
      if (!user || !threadId) return Promise.resolve(false);

      const generation = ++_generation;
      const previousSession = _activeSession;
      const session = createSession(threadId, user, generation);
      _activeSession = session;

      if (previousSession) {
        commit(CLEAR_THREAD, { threadId: previousSession.threadId });
        disposeSession(previousSession);
      }
      commit(CLEAR_THREAD, { threadId });

      const runJoin = async () => {
        if (!isCurrentSession(session)) {
          await cleanupPresence(session);
          return false;
        }

        try {
          await session.presenceRef.onDisconnect().remove();
          if (!isCurrentSession(session)) {
            await cleanupPresence(session);
            return false;
          }

          await session.presenceRef.set({ name: session.name, ts: 0 });
          if (!isCurrentSession(session)) {
            await cleanupPresence(session);
            return false;
          }

          const onTypingChanged = (snap) => {
            if (!isCurrentSession(session)) return;

            const uid = snap.key;
            const payload = snap.val();
            if (uid === session.uid) return;
            if (!payload || typeof payload.ts !== "number") return;
            scheduleExpiry(commit, session, uid, payload);
          };

          const onChildRemoved = (snap) => {
            if (!isCurrentSession(session)) return;

            const uid = snap.key;
            if (uid === session.uid) return;
            clearExpiryTimer(session.threadId, uid);
            commit(REMOVE_USER, { threadId: session.threadId, uid });
          };

          session.typingRef.on("child_added", onTypingChanged);
          session.typingRef.on("child_changed", onTypingChanged);
          session.typingRef.on("child_removed", onChildRemoved);
          session.unsubscribe = () => {
            session.typingRef.off("child_added", onTypingChanged);
            session.typingRef.off("child_changed", onTypingChanged);
            session.typingRef.off("child_removed", onChildRemoved);
          };

          if (!isCurrentSession(session)) {
            await disposeSession(session);
            return false;
          }

          session.joined = true;
          return true;
        } catch (err) {
          console.error("[typing] joinThread error:", err);
          if (_activeSession === session) _activeSession = null;
          commit(CLEAR_THREAD, { threadId: session.threadId });
          await disposeSession(session);
          return false;
        }
      };

      return runJoin();
    },

    /** Membatalkan session aktif secara sinkron, lalu membersihkan resource. */
    [LEAVE_THREAD]({ commit }) {
      _generation += 1;
      const session = _activeSession;
      _activeSession = null;

      if (!session) return Promise.resolve(true);

      session.disposed = true;
      commit(CLEAR_THREAD, { threadId: session.threadId });

      // Cleanup remote sengaja fire-and-forget agar perpindahan UI tidak
      // menunggu ACK Firebase. disposeSession tetap menangani error sendiri.
      disposeSession(session);
      return Promise.resolve(true);
    },

    /**
     * Mengirim refresh typing dan stop saat input kosong/tidak aktif.
     * Action top-level mengambil session aktif saat dipanggil, sehingga cukup
     * memvalidasi threadId. Generation session tersebut lalu diteruskan ke
     * action internal untuk menolak dispatch/timer lama setelah pergantian sesi.
     */
    [HANDLE_TYPING]({ dispatch }, { threadId }) {
      const session = getCurrentSession(threadId);
      if (!session) return false;

      const update = dispatch(UPDATE_TS, {
        threadId,
        generation: session.generation,
      });
      dispatch(RESET_TIMER, {
        threadId,
        generation: session.generation,
      });
      return update;
    },

    [STOP_TYPING]({ dispatch }, { threadId }) {
      const session = getCurrentSession(threadId);
      if (!session) return false;

      if (session.stopTimer) {
        clearTimeout(session.stopTimer);
        session.stopTimer = null;
      }

      return dispatch(RESET_TS, {
        threadId,
        generation: session.generation,
      });
    },

    [UPDATE_TS](_, { threadId, generation }) {
      const session = getCurrentSession(threadId, generation);
      if (!session) return false;
      return enqueueTypingState(session, true);
    },

    [RESET_TS](_, { threadId, generation }) {
      const session = getCurrentSession(threadId, generation);
      if (!session) return false;
      return enqueueTypingState(session, false);
    },

    [RESET_TIMER]({ dispatch }, { threadId, generation }) {
      const session = getCurrentSession(threadId, generation);
      if (!session) return false;

      clearTimeout(session.stopTimer);
      session.stopTimer = setTimeout(() => {
        session.stopTimer = null;
        dispatch(RESET_TS, { threadId, generation });
      }, CLIENT_STOP_MS);
      return true;
    },
  },

  mutations: {
    [ADD_USER](state, { threadId, uid, name, ts }) {
      const threadUsers = state.typingUsersByThread[threadId] || {};
      state.typingUsersByThread = {
        ...state.typingUsersByThread,
        [threadId]: {
          ...threadUsers,
          [uid]: { name, ts },
        },
      };
    },

    [REMOVE_USER](state, { threadId, uid, expectedTs }) {
      const threadUsers = state.typingUsersByThread[threadId];
      const current = threadUsers?.[uid];
      if (!current) return;
      if (expectedTs !== undefined && current.ts !== expectedTs) return;

      const nextThreadUsers = { ...threadUsers };
      delete nextThreadUsers[uid];
      const nextByThread = { ...state.typingUsersByThread };

      if (Object.keys(nextThreadUsers).length) {
        nextByThread[threadId] = nextThreadUsers;
      } else {
        delete nextByThread[threadId];
      }

      state.typingUsersByThread = nextByThread;
    },

    [CLEAR_THREAD](state, { threadId }) {
      if (!state.typingUsersByThread[threadId]) return;
      const nextByThread = { ...state.typingUsersByThread };
      delete nextByThread[threadId];
      state.typingUsersByThread = nextByThread;
    },
  },
};
